/**
 * CursorAdminConnectModal — paste-token form for the Cursor team-admin
 * connector. User pastes a `key_...` key from cursor.com/dashboard →
 * Settings → Cursor Admin API Keys (Team/Enterprise plan only);
 * backend validates against /teams/daily-usage-data before persisting.
 *
 * See OpenAIBillingConnectModal for the shared shape.
 */

import React, { useEffect, useState } from 'react';
import { IonModal, IonButton, IonSpinner } from '@ionic/react';
import { useConnectCursorAdmin } from '../../hooks/useIntegrations';
import '../../pages/IntegrationsPage/IntegrationsPage.css';

export default function CursorAdminConnectModal({ isOpen, onClose, onConnected }) {
  const [adminKey, setAdminKey] = useState('');
  const [error, setError] = useState('');
  const [connecting, setConnecting] = useState(false);
  const mutation = useConnectCursorAdmin();

  useEffect(() => {
    if (isOpen) {
      setAdminKey('');
      setError('');
      setConnecting(false);
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (!adminKey.trim()) {
      setError('Admin key is required.');
      return;
    }
    setConnecting(true);
    setError('');
    mutation.mutate(
      { adminKey: adminKey.trim() },
      {
        onSuccess: () => {
          setConnecting(false);
          onConnected?.();
          onClose?.();
        },
        onError: (err) => {
          setConnecting(false);
          setError(err?.message || 'Failed to connect Cursor Admin. Please check your admin key.');
        },
      },
    );
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={() => onClose?.()}
      className="lark-connect-modal"
      aria-label="Connect Cursor Admin"
    >
      <div
        className="lark-modal-content"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2>Connect Cursor Admin</h2>
        <p className="lark-modal-description">
          Get your admin key from{' '}
          <a
            href="https://cursor.com/dashboard"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--ion-color-primary)' }}
          >
            cursor.com/dashboard
          </a>{' '}
          → Settings → Cursor Admin API Keys (Team/Enterprise plan only). Zibby reads
          per-user usage via <code>/teams/daily-usage-data</code>.
        </p>

        <div className="lark-form-group">
          <label className="lark-form-label" htmlFor="cursor-admin-admin-key">Admin Key</label>
          <input
            id="cursor-admin-admin-key"
            type="password"
            value={adminKey}
            placeholder="key_..."
            onChange={(e) => { setAdminKey(e.target.value || ''); setError(''); }}
            className="lark-form-input"
            disabled={connecting}
            autoComplete="off"
            spellCheck={false}
            aria-invalid={error ? 'true' : 'false'}
          />
        </div>

        {error && <p className="lark-modal-error" role="alert">{error}</p>}

        <div className="lark-modal-buttons">
          <IonButton
            expand="block"
            onClick={handleSubmit}
            disabled={connecting || !adminKey.trim()}
            aria-label="Submit Cursor Admin admin key"
          >
            {connecting ? <IonSpinner name="crescent" /> : 'Connect'}
          </IonButton>
          <IonButton
            expand="block"
            fill="clear"
            onClick={() => onClose?.()}
            disabled={connecting}
            aria-label="Cancel Cursor Admin connection"
          >
            Cancel
          </IonButton>
        </div>
      </div>
    </IonModal>
  );
}
